"use client";
import type { ChartConfig } from "@cmp-src/primitives/chart";
import { UserPropertyType } from "@gettactic/api";
import { InOfficeChartLegend } from "@gettactic/components/src/components/WeeklyOffice/legend/InOfficeChartLegend";
import { WeeklyDataLayout } from "@gettactic/components/src/components/WeeklyOffice/WeeklyDataLayout";
import { useAuthenticated } from "@gettactic/hooks";
import { format } from "date-fns";
import pluralize from "pluralize";
import { useMemo } from "react";
import { DashboardGreeting } from "@/features/dashboard/components/DashboardGreeting";
import { ReservationModals } from "@/features/reserve/components/modals/ReservationModals";
import { SelectUsers } from "@/features/users/components/SelectUsers";
import { api } from "@/lib/api/api";
import { useTeamAttendanceFilters } from "./TeamSchedule/hooks/useTeamAttendanceFilters";
import { TeamAttendanceFilters } from "./TeamSchedule/TeamAttendanceFilters";
import { WeeklyChart } from "./TeamSchedule/WeeklyChart";
import { WeeklyTable } from "./TeamSchedule/WeeklyTable";

const chartConfig: ChartConfig = {
  inOffice: {
    label: "In office",
    color: "hsl(var(--chart-1))",
    indicator: "dot"
  },
  favorite: {
    color: "hsl(var(--chart-2))",
    label: "Favorite",
    indicator: "circle"
  }
};

export const TeamSchedule = ({
  daysCount,
  showAsMobileDaily,
  selectedDayIndex,
  showHeader = true
}: {
  daysCount: number;
  showAsMobileDaily?: boolean;
  selectedDayIndex?: number;
  showHeader?: boolean;
}) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const {
    filters: dashboardFilters,
    isComponentLoading,
    totalUsers,
    tableOverviewData,
    chartData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    setUsersFilter,
    setTeams,
    handleSaveFilterData
  } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice,
    selectedDayIndex
  });

  const { formattedWeek, somethingWentWrong } = useMemo(() => {
    try {
      const formatted =
        dashboardFilters.week?.map((date) => format(date, "yyyy-MM-dd")) || [];
      return { formattedWeek: formatted, somethingWentWrong: false };
    } catch (e) {
      return { formattedWeek: [], somethingWentWrong: true };
    }
  }, [dashboardFilters.week]);

  if (somethingWentWrong) {
    return (
      <div className="flex justify-center p-4">
        Unable to process date information
      </div>
    );
  }

  const weeklyTable = (
    <WeeklyTable
      daysCount={daysCount}
      userSearch={
        !showAsMobileDaily ? (
          <SelectUsers
            className={"hidden md:block"}
            // disabled={isComponentLoading}
            onChange={(ids: string[]) => {
              handleSaveFilterData(UserPropertyType.LastUsersFilter, ids);
              setUsersFilter(ids);
            }}
            values={dashboardFilters.users}
          />
        ) : undefined
      }
      authenticatedUser={authenticatedUser}
      data={tableOverviewData}
      formattedWeek={formattedWeek}
      favsApi={api}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
      showAsMobileDaily={showAsMobileDaily}
      selectedDayIndex={selectedDayIndex}
    />
  );

  if (showAsMobileDaily)
    return (
      <>
        {showHeader && (
          <div className={"flex items-center gap-2 pl-2"}>
            <div className={"flex-1 text-[15px] text-neutral-1000"}>
              <strong>{totalUsers}</strong> {pluralize("people", totalUsers)} in
              office today{" "}
            </div>
            <div>
              <TeamAttendanceFilters
                teams={dashboardFilters.teams}
                onTeamsChange={setTeams}
                onSaveFilterData={handleSaveFilterData}
                isComponentLoading={isComponentLoading}
                triggerClassName={
                  "py-3 px-3.5 rounded-md border-1 border-neutral-100"
                }
              />
            </div>
          </div>
        )}
        <div className={"rounded-lg border-1 border-neutral-200 bg-white px-4"}>
          {weeklyTable}
        </div>
        <ReservationModals />
      </>
    );

  return (
    <div className="mt-4 mb-12 flex flex-row justify-center md:mt-16">
      <div className="flex w-full flex-col space-y-9">
        {showHeader && (
          <div className="flex flex-col gap-4">
            <div className="flex items-end justify-between gap-4">
              <DashboardGreeting
                name={authenticatedUser.user?.name || ""}
                subtitle={
                  <>
                    <span
                      className={"font-inter font-bold text-neutral-1000"}
                      style={{ fontSize: "14px" }}
                    >
                      {totalUsers}
                    </span>{" "}
                    <span
                      className={"font-inter text-neutral-900"}
                      style={{ fontSize: "14px" }}
                    >
                      {totalUsers === 1 ? "person" : "people"} expected to be in
                      the office this week
                    </span>
                  </>
                }
              />
              <div className="flex-shrink-0">
                <TeamAttendanceFilters
                  teams={dashboardFilters.teams}
                  onTeamsChange={setTeams}
                  onSaveFilterData={handleSaveFilterData}
                  isComponentLoading={isComponentLoading}
                  triggerClassName={"pt-3"}
                />
              </div>
            </div>
          </div>
        )}
        <div>
          <WeeklyDataLayout
            chart={
              <WeeklyChart
                daysCount={daysCount}
                isLoading={isLoading}
                chartInfo={{
                  chartData: chartData,
                  chartConfig: chartConfig
                }}
                myId={authenticatedUser.user?.id || ""}
                userCount={totalUsers}
              />
            }
            legend={<InOfficeChartLegend chartConfig={chartConfig} />}
            table={weeklyTable}
            daysCount={daysCount}
          />
        </div>
      </div>
      <ReservationModals />
    </div>
  );
};
